import React from 'react';
import LeadSigningDetails from '../LeadSigningDetails';
import {
  ClientSigningDetailsFields,
  ClientDto,
} from '../../services/clientsService';
import {
  BeneficiaryType,
  LeadSigningDetailsDto,
  SubmitterSourceType,
} from '../../services/leadSigningService';

interface Props {
  /** ערכי שדות החתימה הנוכחיים */
  value: ClientSigningDetailsFields;
  /** שאר השדות מהלקוח שהקומפוננטה הפנימית צריכה לראות (כתובת/עיר/ת.ז./תאריך לידה הראשיים) */
  clientBase?: {
    address?: string;
    city?: string;
    idNumber?: string;
    birthDate?: string;
    isForeignResident?: boolean;
    benefitType?: number;
  };
  errors: Record<string, string>;
  mode: 'create' | 'edit' | 'readonly';
  onChange: (patch: Partial<ClientSigningDetailsFields>) => void;
  onErrorsChange: (errors: Record<string, string>) => void;
}

/**
 * עוטף את LeadSigningDetails ומציג רק את הסעיפים הרלוונטיים ללקוח —
 * סוג מוטב + פרטי הורים/בן זוג + מגיש הבקשה.
 * "פרטי הלקוח" מוסתר כי הם כבר מוצגים בטופס הלקוח הראשי.
 */
const ClientAdditionalDetailsSection: React.FC<Props> = ({
  value,
  clientBase,
  errors,
  mode,
  onChange,
  onErrorsChange,
}) => {
  const disabled = mode === 'readonly';

  // בניית מבנה LeadSigningDetailsDto מתוך שדות שטוחים של ClientDto
  const leadValue: LeadSigningDetailsDto = {
    id: 0,
    benefitType: clientBase?.benefitType ?? 0,
    beneficiaryType: (value.beneficiaryType as BeneficiaryType) ?? 'other',
    clientAddress: clientBase?.address,
    clientCity: clientBase?.city,
    clientIdNumber: clientBase?.idNumber,
    clientBirthDate: clientBase?.birthDate,
    isClientForeignResident: clientBase?.isForeignResident ?? false,
    father: {
      name: value.fatherName,
      idNumber: value.fatherIdNumber,
      address: value.fatherAddress,
      city: value.fatherCity,
      birthDate: value.fatherBirthDate,
      isForeignResident: value.isFatherForeignResident,
    },
    mother: {
      name: value.motherName,
      idNumber: value.motherIdNumber,
      address: value.motherAddress,
      city: value.motherCity,
      birthDate: value.motherBirthDate,
      isForeignResident: value.isMotherForeignResident,
    },
    spouse: {
      name: value.spouseName,
      address: value.spouseAddress,
      city: value.spouseCity,
      idNumber: value.spouseIdNumber,
      birthDate: value.spouseBirthDate,
      isForeignResident: value.isSpouseForeignResident,
    },
    submitter: {
      sourceType: (value.submitterSourceType as SubmitterSourceType) ?? 'other',
      name: value.submitterName,
      relationship: value.submitterRelationship,
      idNumber: value.submitterIdNumber,
      address: value.submitterAddress,
      city: value.submitterCity,
    },
  };

  const handleInnerChange = (next: LeadSigningDetailsDto) => {
    onChange({
      beneficiaryType: next.beneficiaryType,
      // הורים
      fatherName: next.father.name,
      fatherIdNumber: next.father.idNumber,
      fatherAddress: next.father.address,
      fatherCity: next.father.city,
      fatherBirthDate: next.father.birthDate,
      isFatherForeignResident: next.father.isForeignResident,
      motherName: next.mother.name,
      motherIdNumber: next.mother.idNumber,
      motherAddress: next.mother.address,
      motherCity: next.mother.city,
      motherBirthDate: next.mother.birthDate,
      isMotherForeignResident: next.mother.isForeignResident,
      // בן/בת זוג
      spouseName: next.spouse.name,
      spouseAddress: next.spouse.address,
      spouseCity: next.spouse.city,
      spouseIdNumber: next.spouse.idNumber,
      spouseBirthDate: next.spouse.birthDate,
      isSpouseForeignResident: next.spouse.isForeignResident,
      // מגיש
      submitterSourceType: next.submitter.sourceType,
      submitterName: next.submitter.name,
      submitterRelationship: next.submitter.relationship,
      submitterIdNumber: next.submitter.idNumber,
      submitterAddress: next.submitter.address,
      submitterCity: next.submitter.city,
    });
  };

  return (
    <LeadSigningDetails
      value={leadValue}
      errors={errors}
      disabled={disabled}
      hideClientSection
      onChange={handleInnerChange}
      onErrorsChange={onErrorsChange}
    />
  );
};

/**
 * עזר לחילוץ ClientSigningDetailsFields מ-ClientDto (בעת טעינת טופס עריכה).
 */
export const extractClientSigningDetails = (client: ClientDto): ClientSigningDetailsFields => ({
  beneficiaryType: client.beneficiaryType,
  fatherName: client.fatherName,
  fatherIdNumber: client.fatherIdNumber,
  fatherAddress: client.fatherAddress,
  fatherCity: client.fatherCity,
  fatherBirthDate: client.fatherBirthDate,
  isFatherForeignResident: client.isFatherForeignResident ?? false,
  motherName: client.motherName,
  motherIdNumber: client.motherIdNumber,
  motherAddress: client.motherAddress,
  motherCity: client.motherCity,
  motherBirthDate: client.motherBirthDate,
  isMotherForeignResident: client.isMotherForeignResident ?? false,
  spouseName: client.spouseName,
  spouseAddress: client.spouseAddress,
  spouseCity: client.spouseCity,
  spouseIdNumber: client.spouseIdNumber,
  spouseBirthDate: client.spouseBirthDate,
  isSpouseForeignResident: client.isSpouseForeignResident ?? false,
  submitterSourceType: client.submitterSourceType,
  submitterName: client.submitterName,
  submitterRelationship: client.submitterRelationship,
  submitterIdNumber: client.submitterIdNumber,
  submitterAddress: client.submitterAddress,
  submitterCity: client.submitterCity,
});

export default ClientAdditionalDetailsSection;
